import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusBadge } from "../components/ui/status-badge";
import { PerformanceBar } from "../components/ui/performance-bar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatDistanceToNow } from "date-fns";
import { DeviceDiscoverySimulator } from "../components/device-discovery/device-discovery-simulator";
import type { Device } from "../lib/types";
import { getDevices } from "../api/axios";
import { 
  Radar, 
  SearchX, 
  ChevronLeft, 
  ChevronRight, 
  Router, 
  Server, 
  Database, 
  Shield, 
  Network, 
  Loader, 
  Mail, 
  HardDrive, 
  Monitor, 
  Archive, 
  Box 
} from "lucide-react";

export default function Inventory() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialStatusFilter = searchParams.get("status") || "all";

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [typeFilter, setTypeFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: devices, isLoading } = useQuery<Device[]>({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="h-12 bg-gray-200 rounded w-full"></div>
        <div className="space-y-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <div>
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-dark">
                Device Inventory
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and monitor all devices in your network
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">
                    <Radar className="h-4 w-4 mr-2" />
                    Discover Devices
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[450px] sm:w-[540px]">
                  <SheetHeader>
                    <SheetTitle>Network Device Discovery</SheetTitle>
                    <SheetDescription>
                      Automatically detect devices on your network
                    </SheetDescription>
                  </SheetHeader>
                  <div className="py-6">
                    <DeviceDiscoverySimulator 
                      onDevicesFound={(discoveredDevices) => {
                        // In a real implementation, we would add these devices to the inventory
                        console.log("Discovered devices:", discoveredDevices);
                      }} 
                    />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white p-4 shadow rounded-lg border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Input
                type="text"
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full"
              />
            </div>
            <div>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="up">Up</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="down">Down</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select
                value={typeFilter}
                onValueChange={(value) => {
                  setTypeFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Device Type" />
                </SelectTrigger>
                <SelectContent>
                  {["all"].map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === "all" ? "All Types" : type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select
                value={groupFilter}
                onValueChange={(value) => {
                  setGroupFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Device Group" />
                </SelectTrigger>
                <SelectContent>
                  {["all"].map((group) => (
                    <SelectItem key={group} value={group}>
                      {group === "all" ? "All Groups" : group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {/* Device Table */}
        <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
          <div className="py-8 text-center">
            <SearchX className="h-12 w-12 text-gray-400 mb-2" />
            <p className="text-gray-500">No devices found in your inventory.</p>
          </div>
        </div>
      </div>
    );
  }

  const getDeviceIcon = (type: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      "Network Router": <Router className="h-5 w-5" />,
      "Web Server": <Server className="h-5 w-5" />,
      "Database Server": <Database className="h-5 w-5" />,
      Firewall: <Shield className="h-5 w-5" />,
      Switch: <Network className="h-5 w-5" />,
      "Load Balancer": <Loader className="h-5 w-5" />,
      "Mail Server": <Mail className="h-5 w-5" />,
      "Storage Server": <HardDrive className="h-5 w-5" />,
      "Monitoring Server": <Monitor className="h-5 w-5" />,
      "Backup Server": <Archive className="h-5 w-5" />,
    };

    return iconMap[type] || <Box className="h-5 w-5" />;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "up":
        return "text-secondary";
      case "down":
        return "text-danger";
      case "warning":
        return "text-warning";
      default:
        return "text-gray-500";
    }
  };

  // Apply filters to devices
  const filteredDevices = (devices || []).filter((device: any) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        device.name?.toLowerCase().includes(searchLower) ||
        device.ipAddress?.toLowerCase().includes(searchLower) ||
        device.type?.toLowerCase().includes(searchLower) ||
        device.group?.toLowerCase().includes(searchLower) ||
        device.hostname?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== "all" && device.status !== statusFilter) {
      return false;
    }

    // Type filter
    if (typeFilter !== "all" && device.type !== typeFilter) {
      return false;
    }

    // Group filter
    if (groupFilter !== "all" && device.group !== groupFilter) {
      return false;
    }

    return true;
  });

  // Calculate pagination for filtered devices
  const totalPages = Math.ceil(filteredDevices.length / pageSize);
  const paginatedDevices = filteredDevices.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">
              Device Inventory
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and monitor all devices in your network
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">
                  <Radar className="h-4 w-4 mr-2" />
                  Discover Devices
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[450px] sm:w-[540px]">
                <SheetHeader>
                  <SheetTitle>Network Device Discovery</SheetTitle>
                  <SheetDescription>
                    Automatically detect devices on your network
                  </SheetDescription>
                </SheetHeader>
                <div className="py-6">
                  <DeviceDiscoverySimulator 
                    onDevicesFound={(discoveredDevices) => {
                      // In a real implementation, we would add these devices to the inventory
                      console.log("Discovered devices:", discoveredDevices);
                    }} 
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 shadow rounded-lg border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full"
            />
          </div>
          <div>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="up">Up</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="down">Down</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select
              value={typeFilter}
              onValueChange={(value) => {
                setTypeFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Device Type" />
              </SelectTrigger>
              <SelectContent>
                {["all", ...new Set((devices || []).map((device) => device.type))].map((type) => (
                  <SelectItem key={type} value={type}>
                    {type === "all" ? "All Types" : type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select
              value={groupFilter}
              onValueChange={(value) => {
                setGroupFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Device Group" />
              </SelectTrigger>
              <SelectContent>
                {["all", ...new Set((devices || []).map((device) => device.group))].map((group) => (
                  <SelectItem key={group} value={group}>
                    {group === "all" ? "All Groups" : group}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Device Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Device Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        IP Address
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Group
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Performance
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Last Check
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedDevices.map((device) => (
                      <tr key={device._id || device.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`${getStatusClass(device.status)} mr-2`}>
                              {getDeviceIcon(device.type)}
                            </span>
                            <div className="text-sm font-medium text-neutral-dark">
                              {device.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {device.ipAddress}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {device.type}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {device.group}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge
                            status={
                              device.status as
                                | "up"
                                | "down"
                                | "warning"
                                | "unknown"
                            }
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <PerformanceBar value={device.performance} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDistanceToNow(new Date(device.lastCheck), {
                            addSuffix: true,
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Button
                              variant="link"
                              className="text-primary hover:text-primary-dark p-0 h-auto"
                              asChild
                            >
                              <Link to={`/device/${device._id || device.id}`}>Details</Link>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {filteredDevices.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span> to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, filteredDevices.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">
                    {filteredDevices.length}
                  </span>{" "}
                  devices
                  {filteredDevices.length !== (devices || []).length && (
                    <span className="text-gray-500 ml-1">
                      (filtered from {(devices || []).length} total)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <Button
                    variant="outline"
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    onClick={() =>
                      setCurrentPage(Math.max(1, currentPage - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* Simple pagination for brevity */}
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={i}
                        variant={
                          currentPage === pageNum ? "secondary" : "outline"
                        }
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "bg-primary bg-opacity-10 border-primary text-primary"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  {totalPages > 5 && (
                    <>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                      <Button
                        variant="outline"
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outline"
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage >= totalPages}
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
